import { useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSWRConfig } from "swr";
import { IconArrowLeft, IconCheck, IconAlertTriangle } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useMe } from "../../../../swr/useMe";
import { Role } from "../../../../api";
import { useSaleDetails } from "../../hooks/useSaleDetails";
import { useConfirmSaleOrder } from "../../hooks/useConfirmSaleOrder";
import { useMarkSaleReceived } from "../../hooks/useMarkSaleReceived";
import { deriveOrderStatus, hasOrderShipped, isOrderDelivered } from "../../utils/order-status";
import { OrderStatusBanner } from "../components/order-status-banner";
import { OrderItems } from "../components/order-items";
import { OrderTimeline } from "../components/order-timeline";
import { OrderSidebar } from "../components/order-sidebar";
import { OrderDocumentButton } from "../components/order-document-button";
import { SaleDeliveryInformation } from "../components/sale-delivery-information";
import { ReportDisputeModal } from "../../../order-disputes/presentation/components/report-dispute-modal";
import { CreateWarrantyClaimDialog } from "../../../warranty-claims/presentation/components/create-warranty-claim-dialog";

export function SaleDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const { confirmSaleOrder } = useConfirmSaleOrder();
  const { markSaleReceived } = useMarkSaleReceived();
  const { user } = useMe();

  const numericId = id != null ? Number(id) : NaN;
  const validId = Number.isFinite(numericId);
  const { data, error, isLoading } = useSaleDetails(validId ? numericId : null);

  const [confirmingSale, setConfirmingSale] = useState(false);
  const [confirmingReceipt, setConfirmingReceipt] = useState(false);
  const [reportingIssue, setReportingIssue] = useState(false);
  const [claimLine, setClaimLine] = useState<{ lineId: number; productName: string } | null>(null);

  const handleBack = useCallback(() => navigate(AppRoutes.ORDERS), [navigate]);

  const handleConfirm = useCallback(async () => {
    if (!data) return;
    setConfirmingReceipt(true);
    const ok = await markSaleReceived(data.id);
    setConfirmingReceipt(false);
    if (ok) {
      await mutate(`sale-details-${data.id}`);
      await mutate((key) => typeof key === "string" && key.startsWith("orders-"));
    }
  }, [data, markSaleReceived, mutate]);

  const handleConfirmSale = useCallback(async () => {
    if (!data) return;
    setConfirmingSale(true);
    const ok = await confirmSaleOrder(data.id);
    setConfirmingSale(false);
    if (ok) {
      await mutate(`sale-details-${data.id}`);
      await mutate((key) => typeof key === "string" && key.startsWith("orders-"));
    }
  }, [data, confirmSaleOrder, mutate]);

  if (!validId) return <NotFound onBack={handleBack} />;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 px-16 py-8">
        <ShimmerLoader message="Loading order details…">
          <div className="flex flex-col gap-4">
            <Shimmer className="h-6 w-1/3" />
            <Shimmer className="h-20 w-full" />
            <div className="grid grid-cols-5 gap-6">
              <Shimmer className="col-span-3 h-96 w-full" />
              <Shimmer className="col-span-2 h-96 w-full" />
            </div>
          </div>
        </ShimmerLoader>
      </div>
    );
  }

  if (error || !data) return <NotFound onBack={handleBack} />;

  const status = deriveOrderStatus(data);
  const statusSubtitle: Record<typeof status, string> = {
    processing: "The seller has received your order and is preparing it for delivery.",
    inTransit: "Your order is on its way to the delivery address.",
    delivered: "Your order has been delivered. Please confirm receipt to close it out.",
    validated: "Your order has been delivered and receipt confirmed.",
    cancelled: "This order was cancelled and will not be fulfilled.",
    rejected: "This order was rejected by the seller.",
  };
  const currency = data.currency?.name ?? "QAR";
  const isAdmin = user?.role === Role.Admin;
  const hasShipment = (data.dropshipping?.length ?? 0) > 0;
  const shipmentDates = (data.dropshipping ?? [])
    .map((shipment) => shipment.scheduledDate ?? shipment.dateDeadline)
    .filter((value): value is string => !!value);
  const earliestShipmentDate = shipmentDates.reduce<string | null>((earliest, current) => {
    if (!earliest) return current;
    return new Date(current).getTime() < new Date(earliest).getTime() ? current : earliest;
  }, null);
  const latestShipmentDate = shipmentDates.reduce<string | null>((latest, current) => {
    if (!latest) return current;
    return new Date(current).getTime() > new Date(latest).getTime() ? current : latest;
  }, null);
  const canConfirmSale = data.state?.toLowerCase() === "draft" || data.state?.toLowerCase() === "sent";
  const canConfirmReceipt = status === "delivered" && !data.buyerReceived;
  // Same lifecycle gates the API enforces: issues open on any confirmed order
  // (before shipping only for a late / missing delivery or a billing problem),
  // warranty claims once the goods have been delivered.
  const hasShipped = hasOrderShipped(status);
  const canReportIssue = hasShipped || data.state?.toLowerCase() === "sale";
  const canFileClaim = isOrderDelivered(status);

  return (
    <div className="flex flex-col gap-6 px-16 py-8 max-md:gap-4 max-md:px-4 max-md:py-6">
      <div className="flex">
        <Button
          variant="ghostAccent"
          leftIcon={<IconArrowLeft size={16} />}
          onClick={handleBack}
          className="-ml-2.5"
        >
          Back to Orders
        </Button>
      </div>

      <div className="flex items-end justify-between gap-4 max-md:flex-col max-md:items-stretch">
        <div>
          <Heading variant="h2" tone="dark">Order - {data.name}</Heading>
          <Text variant="bodyLg" tone="muted">{statusSubtitle[status] ?? "Track and manage your order"}</Text>
        </div>
        <div className="flex items-center gap-3 shrink-0 max-md:flex-wrap">
          {canConfirmSale && (
            <Button
              variant="primary"
              leftIcon={<IconCheck size={16} />}
              onClick={handleConfirmSale}
              disabled={confirmingSale}
            >
              {confirmingSale ? "Confirming…" : "Confirm Order"}
            </Button>
          )}
          {canConfirmReceipt && (
            <Button
              variant="primaryGreen"
              leftIcon={<IconCheck size={16} />}
              onClick={handleConfirm}
              disabled={confirmingReceipt}
            >
              {confirmingReceipt ? "Confirming…" : "Mark as Received"}
            </Button>
          )}
          {canReportIssue && (
            <Button
              variant="secondary"
              leftIcon={<IconAlertTriangle size={16} />}
              onClick={() => setReportingIssue(true)}
            >
              Report an Issue
            </Button>
          )}
          <OrderDocumentButton order={data} status={status} />
        </div>
      </div>

      <OrderStatusBanner
        status={status}
        statusChangedAt={data.dateOrder}
        estimatedDelivery={
          latestShipmentDate ?? null
        }
      />

      {status !== "processing" && (
        <SaleDeliveryInformation dropshipping={data.dropshipping} showInternalCosts={isAdmin} />
      )}

      <div className="grid grid-cols-5 items-start gap-6 max-md:grid-cols-1 max-md:gap-4">
        <div className="col-span-3 flex flex-col gap-6 max-md:col-span-1 max-md:gap-4">
          <OrderItems
            orderLines={data.orderLines ?? []}
            currency={currency}
            onFileClaim={canFileClaim ? (lineId, productName) => setClaimLine({ lineId, productName }) : undefined}
          />
          <OrderTimeline
            status={status}
            dateOrder={data.dateOrder}
            shippedAt={hasShipment ? earliestShipmentDate ?? data.dateOrder : null}
            deliveredAt={status === "delivered" ? data.dateOrder : null}
          />
        </div>
        <div className="col-span-2 max-md:col-span-1">
          <OrderSidebar sale={data} showInternalCosts={isAdmin} />
        </div>
      </div>

      <ReportDisputeModal
        isOpen={reportingIssue}
        saleOrderId={data.id}
        orderName={data.name}
        hasShipped={hasShipped}
        onClose={() => setReportingIssue(false)}
      />
      {claimLine && (
        <CreateWarrantyClaimDialog
          open={true}
          onClose={() => setClaimLine(null)}
          saleOrderId={data.id}
          saleOrderLineId={claimLine.lineId}
          productName={claimLine.productName}
        />
      )}
    </div>
  );
}

function NotFound({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex flex-col gap-4 px-16 py-12 text-center">
      <Heading variant="h3" tone="dark">Order not found</Heading>
      <Text variant="bodySm" tone="muted">
        The order you're looking for doesn't exist or you don't have access to it.
      </Text>
      <div className="flex justify-center">
        <Button variant="secondary" size="sm" onClick={onBack}>Back to orders</Button>
      </div>
    </div>
  );
}
